import { apiFetch } from "./http";
export type AnalyzeRequest = {
  question: string;
  technical_file_id?: number;
  top_k?: number;
  filters?: string[];
  answer_lang?: string; // panelden secilen cevap dili (en|tr|de|fr|es)
};

export type RegulationChunk = {
  id: string;
  regulation: string;
  article: string;
  annex?: string;
  heading: string;
  content: string;
  lang: string;
};

export type PipelineStep = {
  stage: "retrieval" | "generation";
  engine: string;
  model: string;
  detail: string;
  dur_ms: number;
  items_in?: number;
  items_out?: number;
};

export type AnalyzeResponse = {
  answer: string;
  citations: RegulationChunk[];
  model: string;
  pipeline: PipelineStep[];
  chunks_used: number;
  usage_ms: number;
};

export type SourceDocument = {
  name: string;
  type: "pdf" | "csv" | "txt" | "docx";
  regulation?: string;
};

export type SourcesResponse = {
  count: number;
  data_dir: string;
  documents: SourceDocument[];
};

const API_BASE = "/api/v1";

export type AnalysisRecord = {
  id: string;
  question: string;
  answer: string;
  chunks_used: number;
  model: string;
  usage_ms: number;
  created_at: string;
};

export type AnalysesListResponse = {
  count: number;
  limit: number;
  analyses: AnalysisRecord[] | null;
};

export async function listAnalyses(limit = 20): Promise<AnalysesListResponse> {
  const res = await apiFetch(`/api/v1/analyses?limit=${limit}`);
  if (!res.ok) throw new Error(`listAnalyses: HTTP ${res.status}`);
  return res.json();
}

// Settings — MÜŞTERİ BAZLI (per-org) ayarlar. API key/model/bütçe artık burada
// DEĞİL; onlar admin tarafında global olarak yönetilir ve tüm müşterilerde ortaktır.
export type Settings = {
  org_name: string;
  logo_data_url: string;
  docx_footer: string;
  tf_code_prefix: string;
  risk_file_code: string;
  default_ai_provider: string; // "openai" | "gemini" | "deepseek"
  answer_lang: string;         // cevap dili tercihi (server-side, müşteri bazlı)
  updated_at?: string;
};

export type SettingsUpdate = {
  org_name?: string;
  logo_data_url?: string;
  docx_footer?: string;
  tf_code_prefix?: string;
  risk_file_code?: string;
  default_ai_provider?: string; // "openai" | "gemini" | "deepseek"
  answer_lang?: string;
};

export type UploadResult = {
  name: string;
  path: string;
  size: number;
  extension: string;
  regulation?: string;
  note?: string;
};

export type SourcesStats = {
  files: number;
  chunks: number;
  is_indexing: boolean;
  index: string;
};

export async function getSourcesStats(): Promise<SourcesStats> {
  const res = await apiFetch(`${API_BASE}/sources/stats`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export type IngestResult = {
  files: number;
  indexed: number;
  skipped: number;
  chunks: number;
  errors?: string[];
  index_reset: boolean;
  batch_size: number;
};

export async function ingestSources(opts?: { reset?: boolean; only?: string[] }): Promise<IngestResult> {
  const params = new URLSearchParams();
  if (opts?.reset) params.set("reset", "true");
  if (opts?.only && opts.only.length > 0) params.set("only", opts.only.join(","));
  const qs = params.toString();
  const res = await apiFetch(`${API_BASE}/sources/ingest${qs ? `?${qs}` : ""}`, {
    method: "POST",
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function uploadSource(file: File, subdir?: string): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", file);
  if (subdir) form.append("subdir", subdir);
  const res = await apiFetch(`${API_BASE}/sources/upload`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

// Me — giriş yapan kullanıcının org bağlamı (rol + kredi bakiyesi). Taze okunur.
export type Me = {
  role: string;
  org_id: number;
  credit_balance: number;
  org_name?: string;
  status?: string;
  doc_price_usd?: number;
  usd_try_rate?: number;
  doc_price_tl?: number;
  // EUR yalnız havale bölümü için (hesaplarımızda EUR hesabı da var).
  // Kart/PayPal tahsilatında kullanılmaz; kur 0 ise tutar gösterilmez.
  usd_eur_rate?: number;
  doc_price_eur?: number;
  price_gspr_generation_credits?: number; // GSPR üretiminin kredi maliyeti (finalize)
  price_doc_analysis_credits?: number; // Doküman Analizi kredi maliyeti (varsayılan 250)
  price_doc_edit_credits?: number; // Analize göre doküman düzenleme (varsayılan 500; gelecek faz)
  price_translation_credits?: number; // Doküman çevirisi kredi maliyeti (varsayılan 100; doküman başına)
  doc_analysis_active?: boolean; // Doküman Analizi özelliği aktif mi (admin bayrağı)
  translation_active?: boolean; // Doküman Çevirisi özelliği aktif mi (admin bayrağı)
  usage_doc_analysis?: number; // Home metriği: yapılan doküman analizi sayısı
  // Açık ödeme yöntemleri (backend'de yapılandırılmış olanlar). "garanti" her
  // zaman var; "paypal" yalnız kimlik bilgileri tanımlıysa. Tek yöntem varsa
  // panel seçici göstermez — kullanıcı çalışmayan bir seçenekle karşılaşmasın.
  payment_providers?: PaymentProvider[];
  // Havale/EFT hesapları. Kaynak kök dizindeki bank-accounts.json — tanıtım
  // sitesindeki "Banka Hesapları" sayfası da aynı dosyadan üretilir, yani
  // panelde ve sitede farklı IBAN görünemez. Alan yoksa bölüm gösterilmez.
  bank_accounts?: BankInfo;
};

export type PaymentProvider = "garanti" | "paypal";

export type BankAccount = { code: string; iban: string };
export type BankInfo = {
  holder: string;
  bank: string;
  swift: string;
  accounts: BankAccount[];
};

export async function getMe(): Promise<Me> {
  const res = await apiFetch("/api/v1/me");
  if (!res.ok) throw new Error(`getMe: HTTP ${res.status}`);
  return res.json();
}

export async function getSettings(): Promise<Settings> {
  const res = await apiFetch("/api/v1/settings");
  if (!res.ok) throw new Error(`getSettings: HTTP ${res.status}`);
  return res.json();
}

export async function updateSettings(patch: SettingsUpdate): Promise<Settings> {
  const res = await apiFetch("/api/v1/settings", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`updateSettings: HTTP ${res.status}: ${txt}`);
  }
  return res.json();
}

export async function deleteAnalysis(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/analyses/${id}`, { method: "DELETE" });
  if (!res.ok && res.status !== 404) throw new Error(`deleteAnalysis: HTTP ${res.status}`);
}

export async function getSources(): Promise<SourcesResponse> {
  const res = await apiFetch(`${API_BASE}/sources`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function analyze(req: AnalyzeRequest): Promise<AnalyzeResponse> {
  const res = await apiFetch(`${API_BASE}/ai/analyze`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`HTTP ${res.status}: ${txt}`);
  }
  return res.json();
}

// "AI (provider) Answer" — secili saglayiciya chat tarzi soru (konu kilitli,
// dokuman kaynagi yok). provider bos ise backend Settings.DefaultAIProvider kullanir.
export type AskResponse = {
  answer: string;
  provider: string; // "openai" | "gemini" | "deepseek"
  model: string;
};

export async function askAI(question: string, answerLang?: string, provider?: string): Promise<AskResponse> {
  const res = await apiFetch(`${API_BASE}/ai/ask`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, provider, answer_lang: answerLang }),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`HTTP ${res.status}: ${txt}`);
  }
  return res.json();
}
